import { FC, useEffect, useMemo } from 'react'

import { Carat, ExpansionPanel, RotateClockwise } from '@syconium/little-miss-figgy'

import { reportClientError } from '../../../../../app/_components/chrome/scripts/DataDogRumScript'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { summarizeEmbroideryGroup } from '../../../../../containers/EmbroideryContainer/utils/summarizeEmbroideryGroup'
import { EmbroideryGroupContent } from '../../../../embroidery/EmbroideryGroupContent/EmbroideryGroupContent'
import { EmbroideryGroup, toEmbroideryLine } from '../../utils'
import { PriceAndDeleteButton } from '../PriceAndDeleteButton/PriceAndDeleteButton'

import { Body, Inner, Label, StyledExpansionPanelSummary, Top } from './styles'

interface Props {
  group: EmbroideryGroup
  label: string
  removeGroup(): void
  inKit?: boolean
  isLogoSetupInCart: boolean
}

export const EmbroideryGroupItem: FC<Props> = ({
  group,
  label,
  removeGroup,
  inKit,
  isLogoSetupInCart,
}) => {
  const {
    embroidery: {
      cart: { label: embroideryCartLabel },
    },
  } = useTranslation()

  const summary = useMemo(
    () => summarizeEmbroideryGroup(group.lines.map(toEmbroideryLine)),
    [group]
  )

  useEffect(() => {
    if (summary.layout === 'byType') {
      summary.unrecognizedLines.forEach(l => {
        reportClientError({
          error: new Error(`Unrecognized Embroidery Line Type: ${l.properties._type}`),
          context: {
            scope: 'Cart',
            label: 'invalidEmbroideryAttribute',
            guid: group.guid,
            key: l.key,
          },
        })
      })
    }
  }, [group.guid, summary])

  return (
    <ExpansionPanel.Container defaultIsExpanded={!inKit}>
      <Body $inKit={inKit} data-testid='embroidery-cart-item'>
        <Inner $inKit={inKit}>
          <Top>
            <StyledExpansionPanelSummary
              aria-label={embroideryCartLabel}
              expandIcon={isExpanded => (
                <Carat
                  rotateClockwise={
                    isExpanded ? RotateClockwise.QuarterReverse : RotateClockwise.Quarter
                  }
                  transition='0.3s'
                />
              )}
            >
              <Label>{label}</Label>
            </StyledExpansionPanelSummary>
            <PriceAndDeleteButton
              effectivePrice={summary.total.effectivePrice}
              fullPrice={summary.total.fullPrice}
              removeItem={removeGroup}
              title={label}
            />
          </Top>
          <ExpansionPanel.Details>
            <EmbroideryGroupContent summary={summary} hasLogoSetup={isLogoSetupInCart} />
          </ExpansionPanel.Details>
        </Inner>
      </Body>
    </ExpansionPanel.Container>
  )
}
